/**
 * On-device pre-check for Community posts — the same free browser model the
 * AI notes feature uses (Chrome's built-in Gemini Nano, Prompt API). Runs in
 * the composer before submit, so an honest slip ("wrote Mary Alvarez, 71, of
 * 12 Oak St…") is caught on the author's own machine before the text ever
 * leaves it. Advisory only: the server rails (regex firewall, own-lead check,
 * promotion score, duplicate check, optional Gemini) are the enforcement.
 */

type Availability = "unavailable" | "downloadable" | "downloading" | "available";

interface LanguageModelSession {
  prompt(input: string, opts?: { responseConstraint?: object; signal?: AbortSignal }): Promise<string>;
  destroy(): void;
}
interface LanguageModelStatic {
  availability(opts?: object): Promise<Availability>;
  create(opts?: {
    initialPrompts?: Array<{ role: "system" | "user" | "assistant"; content: string }>;
    signal?: AbortSignal;
  }): Promise<LanguageModelSession>;
}

const languageModel = (): LanguageModelStatic | null => {
  if (typeof window === "undefined") return null;
  const lm = (globalThis as any).LanguageModel;
  return lm && typeof lm.availability === "function" ? lm : null;
};

export interface ContentCheck {
  pii: boolean;
  promo: boolean;
  reason: string;
}

const SCHEMA = {
  type: "object",
  properties: {
    pii: { type: "boolean", description: "true if the text identifies a real client, applicant, insured or beneficiary" },
    promo: { type: "boolean", description: "true if the text is a recruiting, vendor, course or affiliate pitch" },
    reason: { type: "string", description: "at most 12 words, no names" },
  },
  required: ["pii", "promo", "reason"],
  additionalProperties: false,
} as const;

const SYSTEM = [
  "You screen a post for a public forum where licensed life-insurance agents talk shop.",
  "pii is true when the text identifies a REAL client, applicant, insured or beneficiary: a person's name together with health, policy, financial or contact details; or any SSN, date of birth, Medicare ID, policy or account number, home address, or a client's phone or email.",
  "Describing a case generically (age, sex, state, conditions, coverage) is fine. An agent sharing their OWN business contact info is fine.",
  "promo is true when the text is a pitch: recruiting (join my team, contracts, comp levels), lead-vendor or software ads, selling courses or coaching, affiliate links, or contact-bait such as DM me for details.",
  "Honest questions, reviews and recommendations of vendors or carriers are fine.",
].join(" ");

/**
 * Returns null when the on-device model isn't ready (not downloaded, not
 * Chrome desktop, policy-disabled) — the caller just skips the pre-check.
 * Never triggers a download: that needs a user gesture and belongs to the
 * InsuraBot summary button, not a silent pre-check.
 */
export async function checkContentOnDevice(title: string, body: string, signal?: AbortSignal): Promise<ContentCheck | null> {
  const lm = languageModel();
  if (!lm) return null;
  try {
    if ((await lm.availability()) !== "available") return null;
    const session = await lm.create({ initialPrompts: [{ role: "system", content: SYSTEM }], signal });
    try {
      const raw = await session.prompt(`TITLE: ${title}\n\nPOST: ${body.slice(0, 6000)}`, { responseConstraint: SCHEMA, signal });
      const match = raw.match(/\{[\s\S]*\}/);
      const parsed = JSON.parse(match ? match[0] : raw);
      return { pii: parsed.pii === true, promo: parsed.promo === true, reason: String(parsed.reason || "").slice(0, 120) };
    } finally {
      session.destroy();
    }
  } catch {
    return null;
  }
}
